import { Injectable, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, map, of, switchMap, Observable } from 'rxjs';
import { AuthService } from '../../../core/services/auth.service';
import { StorageService } from '../../../core/services/storage.service';
import { AuthError, AuthState, AuthUser } from '../../../core/models/auth.model';
import { UserType } from '../../../core/models/user-type.model';
import { SelfProfileService } from '../../../core/profiles/services/self-profile.service';
import { EnrollmentType, StoredSelfProfile } from '../../../core/profiles/models/self-profile.model';
import { mapModalityCodeToEnrollmentType } from '../../../core/profiles/utils/enrollment-type.util';
import { EnrollmentService } from '../../../core/enrollments/services/enrollment-service';
import { StudentProgressService } from '../../../core/student-progress/services/student-progress.service';
import { TutorialGateService } from '../../../core/student-progress/services/tutorial-gate.service';
import { CourseScheduleService } from '../../../core/courses/course-schedules/services/course-schedule-service';
import { ProfileFacade } from '../../../core/profiles/facade/profile.facade';
import { LegalAcceptanceService } from '../../../core/services/legal-acceptance.service';
import { PendingAcceptances } from '../../../core/models/pending-acceptances.model';

const DEFAULT_ENROLLMENT_TYPE: EnrollmentType = 'SYNCHRONOUS';

const LEGAL_CHECK_LOGIN_TYPE = 'BO_STUDENT';

const USER_TYPE_TO_LOGIN_TYPE: Record<UserType, string | null> = {
  nuevo: null,
  socio: 'BO_STUDENT',
  colaborador: 'klab_student',
  ribera: 'RIBERA_STUDENT',
};

@Injectable({ providedIn: 'root' })
export class AuthFacade {
  private readonly authService = inject(AuthService);
  private readonly storage = inject(StorageService);
  private readonly selfProfileService = inject(SelfProfileService);
  private readonly enrollmentService = inject(EnrollmentService);
  private readonly studentProgressService = inject(StudentProgressService);
  private readonly tutorialGateService = inject(TutorialGateService);
  private readonly courseScheduleService = inject(CourseScheduleService);
  private readonly profileFacade = inject(ProfileFacade);
  private readonly legalAcceptanceService = inject(LegalAcceptanceService);
  private readonly router = inject(Router);

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', () => {
        if (this.isAuthenticated()) {
          this.checkSessionStatus().subscribe();
        }
      });
    }
  }

  private readonly _state = signal<AuthState>(this.getInitialState());

  private readonly _selectedUserType = signal<UserType | null>(null);
  private readonly _loginStep = signal<'user-type' | 'credentials'>('user-type');
  private readonly _pendingAcceptances = signal<PendingAcceptances | null>(null);

  readonly isLoading = computed(() => this._state().isLoading);
  readonly error = computed(() => this._state().error);
  readonly user = computed(() => this._state().user);
  readonly isAuthenticated = computed(() => !!this._state().user);

  readonly selectedUserType = this._selectedUserType.asReadonly();
  readonly loginStep = this._loginStep.asReadonly();
  readonly pendingAcceptances = this._pendingAcceptances.asReadonly();

  private getInitialState(): AuthState {
    const token = this.storage.getToken();
    const user = this.storage.getUser<AuthUser>();
    return user
      ? { user, token: 'cookie-session', isLoading: false, error: null }
      : { user: null, token: null, isLoading: false, error: null };
  }

  selectUserType(type: UserType): void {
    this._selectedUserType.set(type);
    this._loginStep.set('credentials');
    this._state.update(s => ({ ...s, error: null }));
  }

  goBackToUserType(): void {
    this._loginStep.set('user-type');
    this._selectedUserType.set(null);
    this._state.update(s => ({ ...s, error: null }));
  }

  login(email: string, password: string, redirectUrl: string = '/enrollments'): void {
    const userType = this._selectedUserType() ?? 'nuevo';
    const loginType = USER_TYPE_TO_LOGIN_TYPE[userType];

    this._state.update(s => ({ ...s, isLoading: true, error: null }));

    this.authService.login({ email, password, loginType }).subscribe({
      next: ({ token, user }) => {
        const userWithType = { ...user, userType };
        this.checkPendingDocuments(loginType).subscribe((pending) => {
          if (pending) {
            this.blockLoginForPendingDocuments(pending);
            return;
          }
          this.completeLogin(userWithType, token, redirectUrl);
        });
      },
      error: (err: AuthError) => {
        this._state.update(s => ({
          ...s,
          isLoading: false,
          error: err,
        }));
      },
    });
  }

  private checkPendingDocuments(loginType: string | null): Observable<PendingAcceptances | null> {
    return loginType === LEGAL_CHECK_LOGIN_TYPE
      ? this.legalAcceptanceService.getBlockingAcceptances()
      : of(null);
  }


  private blockLoginForPendingDocuments(pending: PendingAcceptances): void {
    this._pendingAcceptances.set(pending);
    this._state.update(s => ({ ...s, isLoading: false, error: null }));
    this.authService.logout();
  }

  dismissPendingDocuments(): void {
    this._pendingAcceptances.set(null);
  }

  private completeLogin(userWithType: AuthUser, token: string, redirectUrl: string): void {
    this.storage.setUser(userWithType);

    this.loadAndStoreProfile(userWithType.id, userWithType.email).subscribe(({ isEnrolled, tutorialApproved }) => {
      this._state.set({
        user: userWithType,
        token,
        isLoading: false,
        error: null,
      });

      let finalRedirect = redirectUrl;
      if (!tutorialApproved) {
        finalRedirect = '/tutorials';
      } else if (finalRedirect === '/enrollments' && isEnrolled) {
        finalRedirect = '/dashboard';
      }

      const onboardingCompleted = typeof window !== 'undefined' && localStorage.getItem('onboardingCompleted') === 'true';
      if (onboardingCompleted) {
        this.router.navigate([finalRedirect]);
      } else {
        this.router.navigate(['/onboarding'], { queryParams: { redirectUrl: finalRedirect } });
      }
    });
  }

  private loadAndStoreProfile(
    idUser: number,
    studentCode?: string | null,
  ): Observable<{ isEnrolled: boolean; tutorialApproved: boolean }> {
    const request$ = this.selfProfileService.getProfileByStudentCode(studentCode!);

    return request$.pipe(
      switchMap((response) => {
        const profile = response.data;
        return this.resolveEnrollmentTypeAndCheck(profile.idUser).pipe(
          map(({ enrollmentType, isEnrolled }) => ({ profile, enrollmentType, isEnrolled })),
        );
      }),
      map(({ profile, enrollmentType, isEnrolled }) => {
        const storedProfile: StoredSelfProfile = {
          ...profile,
          enrollmentType,
        };
        this.storage.setProfile(storedProfile);
        this.profileFacade.setProfile(profile);
        return { profile, isEnrolled };
      }),
      switchMap(({ profile, isEnrolled }) => {
        const code = profile.student?.studentCode;
        if (!code) {
          return of({ isEnrolled, tutorialApproved: true });
        }
        return this.tutorialGateService
          .check(code)
          .pipe(map((progress) => ({ isEnrolled, tutorialApproved: progress?.overallStatus === 'APPROVED' })));
      }),
      catchError((err) => {
        console.error('Failed to load profile during login:', err);
        return of({ isEnrolled: false, tutorialApproved: true });
      })
    );
  }


  private resolveEnrollmentTypeAndCheck(studentId: number): Observable<{ enrollmentType: EnrollmentType, isEnrolled: boolean }> {
    return this.enrollmentService.getEnrolledCourseIds(studentId).pipe(
      switchMap((enrolledResponse) => {
        const enrolledIds = enrolledResponse.data ?? [];
        if (enrolledIds.length === 0) {
          return of({ enrollmentType: DEFAULT_ENROLLMENT_TYPE, isEnrolled: false });
        }

        return this.studentProgressService.getCurrentInProgress(studentId).pipe(
          switchMap((progressResponse) => {
            const idCourseSchedule = progressResponse.data?.idCourseSchedule ?? enrolledIds[0];
            return this.courseScheduleService.getScheduleById(idCourseSchedule);
          }),
          map((scheduleResponse) => ({
            enrollmentType: mapModalityCodeToEnrollmentType(scheduleResponse.data?.modality?.code),
            isEnrolled: true
          })),
        );
      }),
      catchError(() => of({ enrollmentType: DEFAULT_ENROLLMENT_TYPE, isEnrolled: false })),
    );
  }

  checkSessionStatus(): Observable<boolean> {
    const user = this.user();
    if (!user?.id) {
      return of(true);
    }
    return this.authService.getUserIdentityStatus(user.id).pipe(
      map((response) => {
        if (response?.state && response?.data) {
          if (response.data.idStateFk && response.data.idStateFk !== 1) {
            this.logout(true);
            return false;
          }
        }
        return true;
      }),
      catchError((error) => {
        if (error?.status === 401 || error?.status === 404) {
          this.logout(true);
          return of(false);
        }
        return of(true);
      })
    );
  }

  logout(sessionExpired: boolean = false): void {
    this.authService.logout(sessionExpired);
    this.profileFacade.clearProfile();
    this.tutorialGateService.reset();
    this._state.set({ user: null, token: null, isLoading: false, error: null });
    this._loginStep.set('user-type');
    this._selectedUserType.set(null);
    this._pendingAcceptances.set(null);
  }
}
